import { Location } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { NavigationEnd, NavigationSkipped, Router } from '@angular/router';
import { SessionService } from '@auth/auth';
import { AuthService } from '@auth0/auth0-angular';
import {
  catchError,
  distinctUntilChanged,
  filter,
  of,
  ReplaySubject,
  Subject,
  switchMap,
  take,
  takeUntil
} from 'rxjs';
import { EventType } from './enums/enums/event-type';
import { EnvironmentService } from './services/environment/environment.service';
import { LoadedMfeHelper } from './services/loaded-mfe.helper';
import { LoaderService } from './services/loader.service';
import { ShellLoggingService } from './services/shell-logging/shell-logging.service';
import { StylesService } from './services/styles.service';
import { BrowserUtils } from './utils/browser-utils';

type NavigationEndOrSkipped = NavigationEnd | NavigationSkipped;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly user$ = new ReplaySubject<any>();

  // Loader
  @ViewChild('shellLoader', { static: false }) shellLoader!: ElementRef;
  primaryLoadingMessage = 'Loading...';
  secondaryLoadingMessage = 'Please wait a moment.';

  // Flags
  loadingMFE = false;
  showHeader = true;

  private readonly destroy$ = new Subject<void>();

  constructor(
    public auth: AuthService,
    public sessionService: SessionService,
    private readonly environmentService: EnvironmentService,
    private router: Router,
    private readonly stylesService: StylesService,
    private readonly loaderService: LoaderService,
    private readonly location: Location,
    private readonly shellLoggingService: ShellLoggingService
  ) {
    this.onAppLoad();
  }
  private onAppLoad(): void {
    this.setEarlyMFASettings();
    this.stylesService.switchColorsForLoadedMfe();
    this.loaderService.displayLoaderSubject.subscribe({
      next: (displayLoader: boolean) => {
        this.loadingMFE = displayLoader;
      }
    });
  }

  async ngOnInit(): Promise<void> {
    this.loaderService.displayLoaderSubject.next(true);

    await this.stylesService.loadStyleUrls();

    this.sessionService.user$.subscribe((user) => {
      this.user$.next(user);
    });

    // TODO This can be improved
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEndOrSkipped =>
            event instanceof NavigationEnd || event instanceof NavigationSkipped
        )
      )
      .subscribe({
        next: (event: NavigationEndOrSkipped) => {
          this.loaderService.displayLoaderSubject.next(false);
          this.onRouteChange(event);
        }
      });

    this.sessionService.isAuthenticated$
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(),
        filter((isAuthenticated) => isAuthenticated),
        switchMap(() => {
          const eventType = EventType.Successful_Login;
          return this.shellLoggingService
            .log(
              eventType,
              'AuthService: Successful login',
              BrowserUtils.getBrowserInfo()
            )
            .pipe(
              take(1),
              catchError((error) => {
                console.error(`Error logging event ${eventType}:`, error);
                const currentPath = window.location.pathname;
                this.router.navigateByUrl(`${currentPath}/session/error`);
                return of(null);
              })
            );
        })
      )
      .subscribe((response) => {
        if (!response) {
          return;
        }

        this.shellLoggingService.setShellUser(response);
      });
  }

  ngAfterViewInit(): void {
    if (this.shellLoader) {
      this.stylesService.switchColorsForLoadedMfe();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  onMfeSelected(path: string): void {
    LoadedMfeHelper.setLoadedAppWithValidation(path, this.environmentService);
    this.stylesService.switchColorsForLoadedMfe();
  }

  private onRouteChange(event: NavigationEndOrSkipped): void {
    // Hide header when user is unauthorized
    this.showHeader = !(
      (event.url.includes('unauthorized') && event.url.includes('403')) ||
      event.url.includes('mfaoptin')
    );
  }

  private setEarlyMFASettings(): void {
    this.showHeader = !this.location.path().includes('mfaoptin');
  }
}
